import type { ExternalAuthConfig } from 'src/auth.types'
import { WaitingScope } from 'src/components/waiting/waiting.scopes'
import { UserNotification } from 'src/modules/admin/configurations/scopes'
import { AlreadyExportedDocument, ExportDataStatus } from 'src/modules/the-consumer-market/tcm_service'
import { NOOP_VOID, Observable, observe, Scope } from 'wdc-cube'

import type { AlertSeverity } from 'wdc-cube'

export type IDialogScope = Scope & { onClose: () => Promise<void> }

@Observable
export class SignInScope extends Scope {
    @observe() userName?: string
    @observe() userNameError?: string
    @observe() password?: string
    @observe() passwordError?: string
    @observe() rememberMe?: boolean
    @observe() errorMessage?: string

    @observe() externalAuthConfig?: ExternalAuthConfig

    onSignIn = Scope.ASYNC_ACTION
    onForgotPassword = Scope.ASYNC_ACTION

    onChangeRememberMe = Scope.ASYNC_ACTION_BOOLEAN
}

@Observable
export class ResetPasswordScope extends Scope {
    @observe() email?: string
    @observe() emailError?: string

    onResetPassword = Scope.ASYNC_ACTION
    onBackToLogin = Scope.ASYNC_ACTION
}

@Observable
export class ChangePasswordScope extends Scope {
    @observe() waiting?: WaitingScope

    @observe() password?: string
    @observe() passwordConfirmation?: string
    @observe() passwordError?: string
    @observe() showPassword = false
    @observe() showPasswordConfirmation = false

    onChangePassword = Scope.ASYNC_ACTION
    onBackToLogin = Scope.ASYNC_ACTION
}

@Observable
export class AlertScope extends Scope {
    @observe() severity: AlertSeverity = 'info'
    @observe() title?: string
    @observe() message?: string

    onClose = Scope.ASYNC_ACTION
}

@Observable
export class NotificationsScope extends Scope {
    @observe() waiting?: WaitingScope

    @observe() alertModalOpen = false
    @observe() notificationDrawerOpen = false
    @observe() notifications: UserNotification[] = []
    @observe() filteredNotifications: UserNotification[] = []
    @observe() alerts: UserNotification[] = []
    @observe() unreadMessagesByType: Record<string, number> = {
        Todas: 0
    }
    @observe() showRead = false
    @observe() showExpired = false
    @observe() notificationTab = 'Todas' as string
    @observe() notificationTabs = ['Todas'] as string[]

    onNotificationTabChange = Scope.ASYNC_ACTION_STRING
    onMarkNotificationAsRead = Scope.ASYNC_ACTION_NUMBER
    onMarkAllNotificationAsRead = Scope.ASYNC_ACTION
    onReadChange = Scope.ASYNC_ACTION_BOOLEAN
    onShowExpiredChange = Scope.ASYNC_ACTION_BOOLEAN
    onNotificationDrawerToggle = Scope.ASYNC_ACTION
    onAlertModalClose = Scope.ASYNC_ACTION
}

@Observable
export class ReleaseNotesScope extends Scope {
    @observe() unreadCount = 0
    @observe() pageUrl = ''

    onOpenReleaseNotes = Scope.ASYNC_ACTION
}

@Observable
export class BodyScope extends Scope {
    // NOOP
}

@Observable
export class MainScope extends Scope {
    @observe() authenticated = false
    @observe() toolbar?: Scope | null
    @observe() body?: Scope | null
    @observe() dialog?: IDialogScope
    @observe() alert?: AlertScope
    @observe() notificationsScope = new NotificationsScope()
    @observe() releaseNotesScope = new ReleaseNotesScope()
    @observe() drawerOpen = false
    @observe() rdStationEnabled = false
    @observe() theMapEnabled = false
    @observe() theActingEnabled = false
    @observe() theConsumerMarketEnabled = false
    @observe() theConnectionEnabled = false
    @observe() userManagerEnabled = false
    @observe() accessAuditEnabled = false
    @observe() billingEnabled = false
    @observe() mercadoVetEnabled = false
    @observe() analiseDesempenhoEnabled = false
    @observe() motorCreditoEnabled = false
    @observe() mcPjEnabled = false
    @observe() mcPfEnabled = false
    @observe() mcSerasaEnabled = false
    @observe() mcSerasaHistoryEnabled = false
    @observe() mcSerproEnabled = false
    @observe() mcGrupoEnabled = false
    @observe() mcAnaliseEnabled = false
    @observe() mcBorderoEnabled = false
    @observe() mcMonitoramentoEnabled = false
    @observe() activePage = ''

    notify: (severity: AlertSeverity, message: string, duration: number) => void = NOOP_VOID

    onSignOut = Scope.ASYNC_ACTION
    onToggleComplianceMode = Scope.ASYNC_ACTION
    onProfile = Scope.ASYNC_ACTION
    onHome = Scope.ASYNC_ACTION
    onDrawerToggle = Scope.ASYNC_ACTION
    onOverview = Scope.ASYNC_ACTION
    onTheConnection = Scope.ASYNC_ACTION
    onTheConsumerMarket = Scope.ASYNC_ACTION
    onMercadoVet = Scope.ASYNC_ACTION
    onAnaliseDesempenho = Scope.ASYNC_ACTION
    onTheActing = Scope.ASYNC_ACTION
    onTheMap = Scope.ASYNC_ACTION
    onRdStation = Scope.ASYNC_ACTION
    onUserManager = Scope.ASYNC_ACTION
    onGroupManager = Scope.ASYNC_ACTION
    onConfigManager = Scope.ASYNC_ACTION
    onAccessAudit = Scope.ASYNC_ACTION
    onBilling = Scope.ASYNC_ACTION

    // :: Motor de Crédito
    onMcPj = Scope.ASYNC_ACTION
    onMcPf = Scope.ASYNC_ACTION
    onMcSerasa = Scope.ASYNC_ACTION
    onMcSerasaHistory = Scope.ASYNC_ACTION
    onMcSerpro = Scope.ASYNC_ACTION
    onMcGrupo = Scope.ASYNC_ACTION
    onMcAnalise = Scope.ASYNC_ACTION
    onMcBordero = Scope.ASYNC_ACTION
    onMcMonitoramento = Scope.ASYNC_ACTION
}

export type AlreadyExportedRow = AlreadyExportedDocument & { included: boolean }

export function alreadyExportedRowKey(doc: AlreadyExportedDocument) {
    return `${doc.docType}:${doc.docId}`
}

export function excludedExportDocIds(scope: ConfirmExportDataLimitScope) {
    return scope.alreadyExported.filter((row) => !row.included).map((row) => row.docId)
}

export function excludedExportDocIdsByType(scope: ConfirmExportDataLimitScope, docType: string) {
    return scope.alreadyExported.filter((row) => !row.included && row.docType === docType).map((row) => row.docId)
}

@Observable
export class ConfirmExportDataLimitScope extends Scope {
    @observe() open = false
    @observe() isChecking = false
    @observe() isDownloading = false

    @observe() canExport = false
    @observe() unlimitedDataExport = false
    @observe() message = ''
    @observe() qttExportRecords?: number
    @observe() limitExportData?: number
    @observe() currentMonthExportedData?: number
    @observe() requestExportQtdd?: number
    @observe() reportType: 'empresas' | 'socios' | 'medicos' | 'vinculos' | undefined

    @observe() availableExportQtdd = 0

    @observe() newTotal = 0
    @observe() percent = 0
    @observe() exceeded = false

    @observe() blockExportWhenExceeded = false
    @observe() blocked = false

    @observe() alreadyExported: AlreadyExportedRow[] = []
    @observe() allAlreadyExportedIncluded = false
    @observe() canExcludeAlreadyExported = false

    onQttExportRecordsChange = Scope.ASYNC_ACTION_NUMBER
    onCheckLimitExportData = Scope.ASYNC_ACTION_ONE<ExportDataStatus>()
    onToggleAlreadyExported = Scope.ASYNC_ACTION_STRING
    onToggleAllAlreadyExported = Scope.ASYNC_ACTION
    onYesNoClicked = Scope.ASYNC_ACTION_BOOLEAN
}
